import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Crown, Sparkles, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function HealthPlanPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/insights/health-score/deep")
      .then((r) => setData(r.data))
      .catch(() => setData({ locked: true }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-12 text-center text-sm text-muted-foreground">Computing your plan…</div>;

  return (
    <div className="mx-auto max-w-3xl px-6 py-12 md:px-10">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Money OS</p>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl"><Activity className="mr-2 inline h-7 w-7 text-primary" /> Your AI Money Health Plan</h1>

      {data?.locked ? (
        <div className="mt-8 rounded-2xl border border-accent/40 bg-gradient-to-br from-accent/5 via-card to-primary/5 p-8 text-center" data-testid="locked-card">
          <Lock className="mx-auto h-10 w-10 text-accent" />
          <h2 className="mt-4 font-display text-2xl">Unlock your full plan</h2>
          <p className="mt-2 text-sm text-muted-foreground">Premium gives you a personalised, AI-generated 4-paragraph action plan based on your saving, consistency, discipline and growth scores — refreshed every visit.</p>
          <div className="mt-6">
            <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="unlock-btn">
              <Link to="/pricing"><Crown className="mr-2 h-4 w-4" /> Get Pro — £14.99/mo</Link>
            </Button>
          </div>
        </div>
      ) : data ? (
        <div className="mt-8 space-y-6">
          <div className="rounded-2xl border border-primary/30 bg-card p-6">
            <div className="flex items-center gap-3">
              <div className="font-display text-5xl text-primary" data-testid="overall-score">{data.overall}</div>
              <div>
                <div className="text-sm text-muted-foreground">/ 100</div>
                <div className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">{data.band}</div>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {["savings","consistency","discipline","growth"].map(k => (
                <div key={k} className="rounded-xl border border-border bg-background p-3" data-testid={`score-${k}`}>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground capitalize">{k}</div>
                  <div className="mt-1 font-display text-2xl">{data[k]?.score}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6" data-testid="action-plan">
            <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-accent" /><span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Your action plan</span></div>
            <div className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{data.action_plan || "Your plan is loading…"}</div>
          </div>
        </div>
      ) : (
        <p className="mt-8 text-sm text-muted-foreground">Could not load.</p>
      )}
    </div>
  );
}
